import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type ToggleSize = 'sm' | 'md' | 'lg';
type ToggleColor = 'primary' | 'neutral';

@Component({
  selector: 'app-toggle',
  standalone: true,
  template: `
    <label
      class="inline-flex cursor-pointer items-center"
      [class.opacity-50]="disabled"
      [attr.aria-disabled]="disabled"
      [attr.for]="inputId"
    >
      <input
        [id]="inputId"
        type="checkbox"
        class="peer sr-only"
        [checked]="value"
        [disabled]="disabled"
        (change)="onNativeChange($event)"
        (blur)="onTouched()"
        [attr.aria-checked]="value"
        role="switch"
        [attr.aria-label]="ariaLabel || null"
      />
      <div
        class="peer relative rounded-full after:absolute after:rounded-full after:border after:border-white after:bg-white after:transition-transform after:duration-200 after:ease-in-out after:content-[''] dark:border-white dark:bg-gray-700"
        [ngClass]="trackClasses"
      ></div>
      @if (label) {
        <span class="ms-3 font-lato text-sm font-bold leading-[0.5px]">
          {{ label }}
        </span>
      }
    </label>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true,
    },
  ],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleComponent implements ControlValueAccessor {
  @Input() label?: string;

  @Input() size: ToggleSize = 'md';

  @Input() color: ToggleColor = 'primary';

  @Input() disabled = false;

  @Input() ariaLabel?: string;

  @Input() inputId = `toggle-${Math.random().toString(36).slice(2)}`;

  value = false;

  private sizes = {
    sm: {
      track: 'h-5 w-9',
      knob: {
        base: 'after:h-4 after:w-4 after:start-[2px] after:top-[2px]',
        shift: 'peer-checked:after:translate-x-[14px]',
      },
    },
    md: {
      track: 'h-6 w-10',
      knob: {
        base: 'after:h-5 after:w-5 after:start-[2px] after:top-[2px]',
        shift: 'peer-checked:after:translate-x-[16px]',
      },
    },
    lg: {
      track: 'h-7 w-12',
      knob: {
        base: 'after:h-6 after:w-6 after:start-[3px] after:top-[3px]',
        shift: 'peer-checked:after:translate-x-[18px]',
      },
    },
  };

  private colors = {
    primary: { base: 'bg-gray-200', checked: 'peer-checked:bg-[#0D62FF]' },
    neutral: { base: 'bg-gray-300', checked: 'peer-checked:bg-gray-800' },
  };

  get trackClasses(): string {
    const s = this.sizes[this.size];
    const c = this.colors[this.color];
    return [
      s.track,
      c.base,
      s.knob.base,
      'peer-focus:outline-none peer-focus:ring-0 peer-focus:ring-white',
      c.checked,
      s.knob.shift,
      'peer-checked:after:border-white',
      'after:transition-transform after:duration-200 after:ease-in-out',
      'rtl:peer-checked:after:-translate-x-full',
    ].join(' ');
  }

  private onChange: (val: boolean) => void = () => {};

  private cdr = inject(ChangeDetectorRef);

  onTouched: () => void = () => {};

  writeValue(val: boolean): void {
    this.value = !!val;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (val: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onNativeChange(e: Event): void {
    if (this.disabled) return;
    const { checked } = e.target as HTMLInputElement;
    this.value = checked;
    this.onChange(checked);
  }
}
